import React, { useState, useEffect } from 'react';
import { TrendingUp, AlertCircle, CheckCircle2, Zap } from 'lucide-react';

const ProgressAnalytics = ({ drillHistory = [], objectionAttempts = [] }) => {
  const [stats, setStats] = useState({
    byType: {},
    byConcept: {},
    weakAreas: [],
    strongAreas: [],
    overallAverage: 0,
    totalPractice: 0
  });

  useEffect(() => {
    calculateStats();
  }, [drillHistory, objectionAttempts]);

  const calculateStats = () => {
    const allAttempts = [
      ...(drillHistory || []).map(d => ({
        type: 'drill',
        concept: d?.type || 'General',
        score: d?.lumoScore || d?.score || 0,
        date: d?.createdAt || d?.completedDate
      })),
      ...(objectionAttempts || []).map(o => ({
        type: 'objection',
        concept: o?.objectionCategory || 'General',
        score: o?.lumoScore || o?.score || 0,
        date: o?.createdAt || o?.completedDate
      }))
    ];

    if (allAttempts.length === 0) {
      setStats({
        byType: {},
        byConcept: {},
        weakAreas: [],
        strongAreas: [],
        overallAverage: 0,
        totalPractice: 0
      });
      return;
    }

    // Calculate by type
    const byType = {};
    allAttempts.forEach(attempt => {
      if (!byType[attempt.type]) {
        byType[attempt.type] = { scores: [], count: 0, total: 0 };
      }
      byType[attempt.type].scores.push(attempt.score);
      byType[attempt.type].count += 1;
      byType[attempt.type].total += attempt.score;
    });

    // Calculate averages for each type
    Object.keys(byType).forEach(type => {
      byType[type].average = Math.round(byType[type].total / byType[type].count);
    });

    // Calculate by concept
    const byConcept = {};
    allAttempts.forEach(attempt => {
      if (!byConcept[attempt.concept]) {
        byConcept[attempt.concept] = { scores: [], count: 0, total: 0 };
      }
      byConcept[attempt.concept].scores.push(attempt.score);
      byConcept[attempt.concept].count += 1;
      byConcept[attempt.concept].total += attempt.score;
    });

    // Calculate averages for each concept
    Object.keys(byConcept).forEach(concept => {
      byConcept[concept].average = Math.round(byConcept[concept].total / byConcept[concept].count);
    });

    // Find weak and strong areas
    const conceptStats = Object.entries(byConcept)
      .map(([concept, data]) => ({
        concept,
        average: data.average,
        count: data.count
      }))
      .sort((a, b) => a.average - b.average);

    const weakAreas = conceptStats.slice(0, 3);
    const strongAreas = conceptStats.slice(-3).reverse();

    // Calculate overall average
    const overallAverage = Math.round(
      allAttempts.reduce((sum, a) => sum + a.score, 0) / allAttempts.length
    );

    setStats({
      byType,
      byConcept,
      weakAreas,
      strongAreas,
      overallAverage,
      totalPractice: allAttempts.length
    });
  };

  const renderTypeCard = (type, data) => {
    const Icon = type === 'drill' ? Zap : AlertCircle;
    const color = type === 'drill' ? 'text-accent' : 'text-warning';
    const bgColor = type === 'drill' ? 'bg-accent/10' : 'bg-warning/10';

    return (
      <div key={type} className={`${bgColor} rounded-xl p-6 border border-border`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-foreground capitalize">{type}s</h4>
          <Icon className={`${color} w-6 h-6`} />
        </div>
        <div className="space-y-2">
          <div className="flex justify-between items-end">
            <span className="text-muted-foreground text-sm">Average Score</span>
            <span className="text-3xl font-bold text-foreground">{data.average}%</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total Attempts</span>
            <span className="text-foreground font-semibold">{data.count}</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 mt-4">
            <div
              className={`${type === 'drill' ? 'bg-accent' : 'bg-warning'} h-2 rounded-full transition-all duration-300`}
              style={{ width: `${data.average}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderConceptRow = (concept, data, isWeak = false) => {
    const score = data.average;
    const getScoreColor = (score) => {
      if (score >= 80) return 'text-success';
      if (score >= 60) return 'text-accent';
      return 'text-destructive';
    };

    const getScoreBgColor = (score) => {
      if (score >= 80) return 'bg-success/10';
      if (score >= 60) return 'bg-accent/10';
      return 'bg-destructive/10';
    };

    return (
      <div
        key={concept}
        className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 transition-colors"
      >
        <div className="flex-1">
          <p className="font-semibold text-foreground text-sm">{concept}</p>
          <p className="text-xs text-muted-foreground">{data.count} attempts</p>
        </div>
        <div className={`${getScoreBgColor(score)} px-3 py-1 rounded-lg flex items-center gap-2`}>
          {isWeak && score < 60 && (
            <AlertCircle className="w-4 h-4 text-destructive" />
          )}
          {!isWeak && score >= 80 && (
            <CheckCircle2 className="w-4 h-4 text-success" />
          )}
          <span className={`font-bold text-sm ${getScoreColor(score)}`}>{score}%</span>
        </div>
      </div>
    );
  };

  if (stats.totalPractice === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-8 text-center">
        <p className="text-muted-foreground mb-2">No practice data yet</p>
        <p className="text-sm text-muted-foreground">Complete drills and objection practice to see your progress</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Stats */}
      <div className="bg-gradient-to-r from-primary/20 to-accent/20 rounded-2xl p-8 border border-primary/20">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Overall Average</p>
            <p className="text-4xl font-bold text-foreground">{stats.overallAverage}%</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Total Practice</p>
            <p className="text-4xl font-bold text-foreground">{stats.totalPractice}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Types Practiced</p>
            <p className="text-4xl font-bold text-foreground">{Object.keys(stats.byType).length}</p>
          </div>
        </div>
      </div>

      {/* Performance by Type */}
      {Object.keys(stats.byType).length > 0 && (
        <div>
          <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-accent" />
            Performance by Type
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(stats.byType).map(([type, data]) =>
              renderTypeCard(type, data)
            )}
          </div>
        </div>
      )}

      {/* Areas to Focus On */}
      {stats.weakAreas.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-destructive" />
            Areas to Focus On
          </h3>
          <div className="space-y-3">
            {stats.weakAreas.map(area =>
              renderConceptRow(area.concept, stats.byConcept[area.concept], true)
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            💡 Tip: Focus on these areas through micro-lessons and targeted practice to improve
          </p>
        </div>
      )}

      {/* Concepts Breakdown */}
      {Object.keys(stats.byConcept).length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-foreground mb-4">Performance by Concept</h3>
          <div className="space-y-3">
            {Object.entries(stats.byConcept)
              .map(([concept, data]) => ({
                concept,
                ...data,
                average: Math.round(data.total / data.count)
              }))
              .sort((a, b) => b.average - a.average)
              .map(item =>
                renderConceptRow(item.concept, item, false)
              )}
          </div>
        </div>
      )}

      {/* Strengths */}
      {stats.strongAreas.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success" />
            Your Strengths
          </h3>
          <div className="space-y-3">
            {stats.strongAreas.map(area =>
              renderConceptRow(area.concept, stats.byConcept[area.concept], false)
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            ✨ Great work! Keep practicing to maintain these high scores
          </p>
        </div>
      )}
    </div>
  );
};

export default ProgressAnalytics;
